/**
 * The Registry → Features "Sync from Registry" result as one status message.
 */

import type { SyncResult } from '../schemas/admin.schemas';

function summary(result: SyncResult): string {
  const counts: [number, string][] = [
    [result.created.length, 'created'],
    [result.updated.length, 'updated'],
    [result.unchanged.length, 'unchanged'],
    [result.errors.length, 'errors'],
    [result.skipped.length, 'not allowlisted'],
  ];
  const parts = counts.filter(([n]) => n > 0).map(([n, label]) => `${String(n)} ${label}`);
  return parts.length > 0 ? `Synced: ${parts.join(', ')}` : 'No features found in registry';
}

function dependencyLines(result: SyncResult): string[] {
  const lines: string[] = [];
  for (const change of result.dependency_changes) {
    let line = `Auto-added deps for ${change.feature}: ${change.added_deps.join(', ')}`;
    if (change.affected_projects.length > 0) {
      line += ` (projects: ${change.affected_projects.join(', ')})`;
    }
    if (change.affected_packs.length > 0) line += ` (packs: ${change.affected_packs.join(', ')})`;
    lines.push(line);
  }
  if (result.configuration_warnings.length > 0) {
    lines.push('Warnings:');
    for (const w of result.configuration_warnings) {
      lines.push(`  ${w.feature} in ${w.project}: unconfigured options: ${w.unconfigured_options.join(', ')}`);
    }
  }
  return lines;
}

/** Language-pack migration report (design §5): moved, left in place, retired variants. */
function migrationLines(result: SyncResult): string[] {
  const lines: string[] = [];
  if (result.migrated.length > 0 || result.migration_skipped.length > 0) {
    lines.push(`Migrated: ${String(result.migrated.length)}`);
  }
  if (result.migration_skipped.length > 0) {
    lines.push('Not migrated:');
    for (const s of result.migration_skipped) {
      const target = s.to !== null && s.to !== s.from ? ` → ${s.to}` : '';
      const line = s.line ? `, line ${s.line}` : '';
      lines.push(`  ${s.from}${target} (${s.kind}): ${s.reason}${line}`);
    }
  }
  if (result.deactivated_variants.length > 0) {
    lines.push(`Deactivated leftover variant features: ${result.deactivated_variants.join(', ')}`);
  }
  if (result.deactivated_replaced.length > 0) {
    lines.push(`Deactivated replaced features: ${result.deactivated_replaced.join(', ')}`);
  }
  return lines;
}

export function formatSyncResult(result: SyncResult): string {
  const lines = [summary(result)];
  if (result.retired.length > 0) {
    lines.push(`Deactivated (repository gone from the registry): ${result.retired.join(', ')}`);
  }
  return [...lines, ...migrationLines(result), ...dependencyLines(result)].join('\n');
}
